import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiChevronDown } from "@mdi/js";
import { FOOTER } from "./mock";
import brandAwardsApi, { BrandAward } from "../../../api-services/brand-awards-api.service";

// A footer column. On desktop the heading and its links are always shown. On
// mobile (see v2.scss) the heading becomes a tappable accordion toggle and the
// body collapses — only when `collapsible` is set. Non-collapsible columns
// (Support, Payment, Follow) stay expanded on every breakpoint.
const FooterCol: React.FC<{
  title: string;
  collapsible?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ title, collapsible = false, className = "", children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`gd-footer-v2__col ${collapsible ? "gd-footer-v2__col--accordion" : ""} ${className}`.trim()}
      data-open={open}
    >
      {collapsible ? (
        <button type="button" className="gd-footer-v2__col-head" onClick={() => setOpen((v) => !v)}>
          <h4>{title}</h4>
          <Icon className="gd-footer-v2__col-chevron" path={mdiChevronDown} size="20px" />
        </button>
      ) : (
        <h4>{title}</h4>
      )}
      <div className="gd-footer-v2__col-body">{children}</div>
    </div>
  );
};

// Award logos wrap onto as many rows as the viewport needs. Past this many
// rows the block collapses behind a "See more" toggle (mobile included, since
// the narrower column wraps sooner and hits the limit more often).
const AWARDS_COLLAPSED_ROWS = 2;

const AwardRow: React.FC<{ awards: BrandAward[]; badgeStyle?: boolean }> = ({ awards, badgeStyle = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const logosRef = useRef<HTMLDivElement>(null);

  // Row count depends on rendered logo widths (only known once each image has
  // loaded) and on viewport width, so it's measured from the DOM rather than
  // computed from the award count.
  const checkOverflow = useCallback(() => {
    const container = logosRef.current;
    if (!container) return;
    const tops = Array.from(container.querySelectorAll("img")).map((img) => img.offsetTop);
    const rowCount = new Set(tops).size;
    setOverflowing(rowCount > AWARDS_COLLAPSED_ROWS);
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [awards, checkOverflow]);

  if (awards.length === 0) return null;

  return (
    <>
      <div
        ref={logosRef}
        className={`gd-footer-v2__award-logos ${badgeStyle ? "gd-footer-v2__award-logos--badges" : ""} ${overflowing && !expanded ? "gd-footer-v2__award-logos--collapsed" : ""}`.trim()}
      >
        {awards.map((award) => (
          <img
            key={award.id}
            src={award.image?.original || award.image?.thumb}
            alt={award.title}
            onLoad={checkOverflow}
          />
        ))}
      </div>
      {overflowing && (
        <button
          type="button"
          className="gd-footer-v2__awards-toggle"
          data-open={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "See less" : "See more"}
          <Icon className="gd-footer-v2__awards-toggle-chevron" path={mdiChevronDown} size="18px" />
        </button>
      )}
    </>
  );
};

// Redesigned GD site footer. Rendered with the redesigned /web homepage.
export default function Footer() {
  const [awards, setAwards] = useState<BrandAward[]>([]);

  useEffect(() => {
    let active = true;

    brandAwardsApi.getAwards()
      .then((data) => {
        if (active) setAwards(data.filter((award) => award.image?.thumb || award.image?.original));
      })
      .catch(() => {
        if (active) setAwards([]);
      });

    return () => {
      active = false;
    };
  }, []);

  const badgeAwards = awards.filter((award) => award.category === "award");
  const logoAwards = awards.filter((award) => award.category !== "award");

  return (
    <footer id="gd-footer-v2">
      <div className="gd-footer-v2__inner">
        <div className="gd-footer-v2__top">
          <div className="gd-footer-v2__brand">
            <img src="/iceholidays-assets/images/GD_logo.png" alt="Golden Destinations" />
          </div>

          <FooterCol title="Our Brands" collapsible>
            {FOOTER.brands.map((b) => {
              if (!b.href) {
                return (
                  <span key={b.label} className="gd-footer-v2__link">{b.label}</span>
                );
              }
              // External brand sites open in a new tab; internal /web paths use
              // the SPA router.
              return b.href.startsWith("http") ? (
                <a
                  key={b.label}
                  href={b.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gd-footer-v2__link"
                >
                  {b.label}
                </a>
              ) : (
                <Link key={b.label} to={b.href} className="gd-footer-v2__link">{b.label}</Link>
              );
            })}
          </FooterCol>

          <FooterCol title="Company" collapsible>
            {FOOTER.company.map((c) => (
              <Link key={c.label} to={c.href} className="gd-footer-v2__link">{c.label}</Link>
            ))}
          </FooterCol>

          <FooterCol title="Support" className="gd-footer-v2__col--support">
            {FOOTER.support.map((c) => (
              <Link key={c.label} to={c.href} className="gd-footer-v2__link">{c.label}</Link>
            ))}
          </FooterCol>

          <FooterCol title="Payment Method" className="gd-footer-v2__col--payment">
            <div className="gd-footer-v2__payments">
              {FOOTER.payments.map((p) => (
                <img key={p} src={p} alt="Payment method" />
              ))}
            </div>
            <div className="gd-footer-v2__follow">
              <h4 className="gd-footer-v2__section-title">Follow Us</h4>
              <div className="gd-footer-v2__socials">
                {FOOTER.socials.map((s) => (
                  <a key={s.icon} href={s.href} target="_blank" rel="noopener noreferrer">
                    <img src={s.icon} alt="Social link" />
                  </a>
                ))}
              </div>
            </div>
          </FooterCol>
        </div>

        <div className="gd-footer-v2__band">
          {awards.length > 0 && (
            <div className="gd-footer-v2__awards">
              <h4 className="gd-footer-v2__section-title">Recognised for Excellence</h4>
              <AwardRow awards={badgeAwards} badgeStyle />
              {badgeAwards.length > 0 && logoAwards.length > 0 && (
                <div className="gd-footer-v2__awards-divider" />
              )}
              <AwardRow awards={logoAwards} />
            </div>
          )}
        </div>

        <div className="gd-footer-v2__bottom">
          <span>Copyright © 2026 ICE HOLIDAYS SDN BHD. All Rights Reserved.</span>
          <div className="gd-footer-v2__bottom-links">
            <Link to="/web/footer/privacy_policy">Privacy</Link>
            <Link to="/web/footer/terms_and_condition">Terms</Link>
            <span>Cookie settings</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
